import { AppRoutes } from '@app/app-routing.interface';
import { authGuard } from '@app/auth/auth.guard';
import { currentOrgGuard } from '@app/shared/current-org.guard';
import { projectGuard } from '@app/shared/guards/project.guard';
import { redirectTo } from '@app/shared/redirect-to';

export const projectRoutes: AppRoutes = [
  {
    canActivate: [authGuard, projectGuard(), currentOrgGuard],
    children: [
      {
        data: { title: 'Projects' },
        loadComponent: () => import('./projects.component').then((m) => m.ProjectsComponent),
        path: '',
        pathMatch: 'full',
      },
      {
        redirectTo: '/?modal=new-project',
        path: 'add',
        pathMatch: 'full',
      },
      {
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/dashboard'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                offcanvas: 'project-details',
                view: 'configuration',
              },
            }),
          },
          {
            path: 'edit',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                offcanvas: 'project-details',
                view: 'configuration',
              },
            }),
          },
        ],
        data: {
          projectChangeUrl: ['/projects'],
        },
        path: ':projectId',
        runGuardsAndResolvers: 'always',
      },
    ],
    path: 'projects',
    runGuardsAndResolvers: 'always',
  },

  /** CRT 3.0 view redirections. */
  {
    path: 'robots/:projectId',
    children: [
      {
        path: 'dashboard',
        pathMatch: 'full',
        redirectTo: redirectTo({
          url: ['/dashboard'],
          map: {
            projectId: 'projectId',
          },
        }),
      },
      {
        path: 'r/:robotId/suite/add',
        pathMatch: 'full',
        redirectTo: redirectTo({
          url: ['/jobs'],
          map: {
            projectId: 'projectId',
          },
          queryParams: {
            modal: 'new-job',
            type: 'accelerator',
          },
        }),
      },
      {
        path: 'r/:robotId/suite/:suiteId/edit',
        pathMatch: 'full',
        redirectTo: redirectTo({
          url: ['/jobs'],
          map: {
            projectId: 'projectId',
            suiteId: 'jobId',
          },
          queryParams: {
            offcanvas: 'job-details',
            offcanvasTab: 'configuration',
          },
        }),
      },
      {
        path: 'r/:robotId/suite/:suiteId',
        redirectTo: redirectTo({
          url: ['/jobs'],
          map: {
            projectId: 'projectId',
            suiteId: 'jobId',
          },
          queryParams: {
            offcanvas: 'job-details',
            offcanvasTab: 'configuration',
          },
        }),
        pathMatch: 'full',
      },
      {
        path: 'r',
        children: [
          {
            path: 'add',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/robots'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                modal: 'new-robot',
              },
            }),
          },
          {
            path: ':robotId',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/robots'],
              map: {
                projectId: 'projectId',
                robotId: 'robotId',
              },
              queryParams: {
                offcanvas: 'robot-details',
                offcanvasTab: 'configuration',
              },
            }),
          },
          {
            path: ':robotId/edit',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/robots'],
              map: {
                projectId: 'projectId',
                robotId: 'robotId',
              },
              queryParams: {
                offcanvas: 'robot-details',
                offcanvasTab: 'configuration',
              },
            }),
          },
          {
            path: ':robotId',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/robots'],
              map: {
                projectId: 'projectId',
                robotId: 'robotId',
              },
              queryParams: {
                offcanvas: 'robot-details',
                offcanvasTab: 'configuration',
              },
            }),
          },
        ],
      },
      {
        path: 'r/:robotId/suite/:suiteId',
        children: [
          {
            path: 'editor',
            redirectTo: redirectTo({
              map: {
                projectId: 'projectId',
                robotId: 'robotId',
                suiteId: 'jobId',
              },
              url: ['/jobs', ':suiteId', 'editor'],
            }),
          },
          {
            path: 'notifications',
            redirectTo: redirectTo({
              map: {
                projectId: 'projectId',
                suiteId: 'jobId',
              },
              queryParams: {
                offcanvas: 'job-details',
                offcanvasTab: 'notifications',
              },
              url: ['/jobs'],
            }),
          },
          {
            path: 'runs/:buildId/artifacts',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/jobs', ':suiteId', 'runs', ':buildId'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                tab: 'artifacts',
              },
            }),
          },
          {
            path: 'runs/:buildId/logs',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/jobs', ':suiteId', 'runs', ':buildId'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                tab: 'logs',
              },
            }),
          },
          {
            path: 'runs/:buildId/report',
            pathMatch: 'full',
            redirectTo: redirectTo({
              url: ['/jobs', ':suiteId', 'runs', ':buildId'],
              map: {
                projectId: 'projectId',
              },
              queryParams: {
                tab: 'details',
              },
            }),
          },
          {
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: redirectTo({
                  map: {
                    projectId: 'projectId',
                    suiteId: 'jobId',
                  },
                  queryParams: {
                    offcanvas: 'job-details',
                    offcanvasTab: 'configuration',
                  },
                  url: ['/jobs'],
                }),
              },
              {
                path: ':buildId',
                pathMatch: 'full',
                redirectTo: redirectTo({
                  url: ['/jobs', ':suiteId', 'runs', ':buildId'],
                  map: {
                    projectId: 'projectId',
                  },
                }),
              },
            ],
            path: 'runs',
          },
        ],
      },
    ],
  },
  /** End of CRT 3.0 view redirections. */

  {
    canActivate: [authGuard, projectGuard('projectId', '/projects')],
    data: { title: 'Test Jobs' },
    loadComponent: () => import('@app/3.0/jobs/jobs.component').then((m) => m.JobsComponent),
    path: 'projects/:projectId/jobs',
    pathMatch: 'full',
    runGuardsAndResolvers: 'always',
  },
];
